/**
 * GameBanner — the ONE discreet edge-warning surface for urgent gameplay
 * beats (D-331, the grid re-route). It used to be the general-purpose
 * center-screen announcer for everything from "PROCESS CLEAR" to boss
 * arrivals; per the owner's two-tier decision (2026-07-31) narration/praise/
 * status moved to the commentary TV (Vera/Fenrir) and only messages the
 * player must react to RIGHT NOW still land on the board — small, docked at
 * the top-right corner, never covering the play path. One surface for every
 * urgent warning (CORE BREAKER, FIREWALL BREACHED, new-threat telegraphs,
 * …) rather than several competing ones.
 */
import { canvasFont, canvasFontSize } from "../rendering/canvasType.ts";
import { COLORS } from "../core/constants.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { announce } from "./a11y.ts";

/**
 * Glitch-flicker rate (Hz). BL-195 (§I): the banner's letter-flicker was 12 Hz —
 * a per-letter shimmer (not a full-field flash, so it never crossed the WCAG
 * 2.3.1 ≤3 full-screen-flash line), but still above the comfortable flicker
 * floor. Capped to {@link GLITCH_HZ} for everyone; FROZEN entirely under
 * reduced-motion (a static two-tone title, no time-cycle, no jitter).
 */
const GLITCH_HZ = 8;

export class GameBanner {
  private text = "";
  private color: string = COLORS.SENTINEL_GREEN;
  private alpha = 0;
  private timer = 0;
  private duration = 0;
  private glitchMode = false;
  private glitchColor2: string = COLORS.ORACLE_RED;
  private elapsed = 0;
  /** S195 anti-fight law #6 (BL-280) — the active banner's slot priority. */
  private priority_ = 0;

  /** @returns true when the banner landed; false when the priority gate
   *  dropped it (an active higher-priority banner owns the slot). */
  show(
    text: string,
    color: string = COLORS.SENTINEL_GREEN,
    duration = 1500,
    priority = 0,
  ): boolean {
    // The slot stays single (canonical — no queue), but it is now priority-
    // arbitrated (S195 anti-fight law #6): a lower-priority show never
    // replaces an active higher one (it is dropped; e.g. a nested
    // combo-milestone can no longer stomp "PROCESS CLEAR" landing in the same
    // frame). Equal or higher replaces, so escalation upward stays instant.
    if (this.timer > 0 && priority < this.priority_) return false;
    this.text = text;
    this.color = color;
    this.alpha = 1;
    this.timer = duration;
    this.duration = duration;
    this.glitchMode = false;
    this.elapsed = 0;
    this.priority_ = priority;
    // BL-110 — mirror the canvas-only outcome (wave/breach/boss/clear; the glitch
    // variant routes through here too) into the polite live region so a screen
    // reader hears it. The single choke point — every banner funnels through here.
    // Render-only + DOM-guarded; the SimHarness stubs gameBanner, so determinism
    // is untouched.
    announce(text);
    return true;
  }

  /** Show with alternating green/red glitch letter effect */
  showGlitch(
    text: string,
    color1: string,
    color2: string,
    duration = 3000,
    priority = 0,
  ): void {
    if (!this.show(text, color1, duration, priority)) return;
    this.glitchMode = true;
    this.glitchColor2 = color2;
  }

  update(dt: number): void {
    if (this.timer <= 0) return;
    this.timer -= dt;
    this.elapsed += dt;
    this.alpha = Math.max(0, this.timer / this.duration);
  }

  /**
   * D-331: the warning docks top-right, small, own-line. `edgeInset` and
   * `edgePad` are derived from {@link canvasFontSize} (which already reads
   * the shared `getUiScale()`), never a bare px literal — so the corner
   * chip lands at the same READABLE size whether the app is running at the
   * shipped `--ui-scale: 1.25` on desktop or the phone tier's extra
   * `--chrome-scale` (up to 1.5×), the same scale-derivation pattern
   * CommentaryTV's chyron uses (D-329/D-330).
   */
  render(ctx: CanvasRenderingContext2D, width: number, _height: number): void {
    void _height; // kept for call-site parity (renderPipeline passes logicalH)
    if (this.alpha <= 0) return;
    ctx.save();
    ctx.globalAlpha = this.alpha;
    ctx.font = canvasFont("warningEdge");
    ctx.textAlign = "right";
    ctx.textBaseline = "top";

    const edgePad = canvasFontSize("warningEdge") * 0.8;
    const x = width - edgePad;
    const y = edgePad;

    // A small translucent backing pill so the corner chip stays legible over
    // busy grid content (fire/particles/enemies) without growing the text or
    // covering more of the board than a single short line needs.
    const textWidth = ctx.measureText(this.text).width;
    const boxPadX = edgePad * 0.6;
    const boxPadY = edgePad * 0.35;
    const lineH = canvasFontSize("warningEdge") * 1.3;
    ctx.fillStyle = "rgba(4, 8, 6, 0.72)";
    ctx.fillRect(
      x - textWidth - boxPadX * 2,
      y - boxPadY,
      textWidth + boxPadX * 2,
      lineH + boxPadY * 2,
    );

    if (this.glitchMode) {
      this.renderGlitch(ctx, x, y);
    } else {
      ctx.fillStyle = this.color;
      ctx.fillText(this.text, x, y);
    }
    ctx.restore();
  }

  private renderGlitch(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
  ): void {
    // BL-195 motion-a11y: under reduced-motion FREEZE the flicker — a stable
    // two-tone title (cycle 0, no time advance, no random jitter). The banner
    // still reads as the green/red "glitch" beat and announces via aria-live; it
    // just never strobes (photosensitivity-safe). Full-motion keeps the flicker,
    // now capped to GLITCH_HZ (was 12 Hz) — still a per-letter shimmer, never a
    // full-field flash, so the FlashGovernor's red-flash B3 lock is unaffected.
    const reduced = isReducedMotion();
    const cycle = reduced ? 0 : Math.floor(this.elapsed * GLITCH_HZ);
    const chars = this.text.split("");
    // Right-aligned: measure the FULL string first, then walk left→right from
    // the computed start so the glitch letters land exactly where the plain
    // right-aligned fillText would have drawn them.
    const totalWidth = ctx.measureText(this.text).width;
    let cx = x - totalWidth;
    ctx.textAlign = "left"; // per-glyph placement must not re-center each char

    for (let i = 0; i < chars.length; i++) {
      const useAlt = (i + cycle) % 3 === 0;
      ctx.fillStyle = useAlt ? this.glitchColor2 : this.color;
      // Random vertical jitter on glitch letters (frozen under reduced-motion).
      const jitter = useAlt && !reduced ? (Math.random() - 0.5) * 4 : 0;
      ctx.fillText(chars[i]!, cx, y + jitter);
      cx += ctx.measureText(chars[i]!).width;
    }
  }

  get active(): boolean {
    return this.timer > 0;
  }

  reset(): void {
    this.text = "";
    this.alpha = 0;
    this.timer = 0;
    this.glitchMode = false;
    this.elapsed = 0;
  }
}
